import { Component, Input, OnInit } from "@angular/core";

import moment, { Moment } from "moment";
import { head, last, get, isEmpty } from "lodash";
import { HttpCalendarService } from "@root/component/multi-calendar/http-calendar.service";
import { HouseStatusService } from "@root/router/deprecated_and_do_not_use-house/house-status/house-status.service";
import { StoreService } from "@root/shared/service/store.service";
import { PlatformType } from "@root/config";
import { Subject, distinctUntilChanged, share, takeUntil, tap } from "rxjs";
import {
  Child,
  Parent,
  MultiHouseCalendarService,
  SelectedData,
  TabName,
  entityType,
} from "../../calendar/multi-house-calendar/multi-house-calendar.service";
import { AppService } from "@root/app.service";

@Component({
  selector: "host-operate-hostex-sidebar",
  templateUrl: "./hostex-sidebar.component.html",
  styleUrls: ["./hostex-sidebar.component.scss"],
})
export class HostexSidebarComponent implements OnInit {
  @Input() selectedData: SelectedData<entityType, "status">;
  index = 0;

  /**
   * 当前实体
   * 如果选择的是房间，entity代表的是房间数据，类型为Child
   * 如果选择的是房型，entity代表的是房型数据，类型为Parent
   * Child可以通过c_parent属性反向寻找到自己的Parent相关的信息
   */
  entity: Child | Parent;
  entityType: "house" | "type";

  dateRange: [Date?, Date?] = [];

  listOfForbiddenDates = [];
  listOfHostexPriceHouses = [];
  listOfHostexPricePrices = [];
  listOfRulesRelevantUnits = []; // rules

  get allowModifyAvailable() {
    const h = moment(head(this.dateRange));
    return h.isSameOrAfter(moment().subtract(1, "d"), "d");
  }

  get nights() {
    const h = moment(head(this.dateRange));
    const l = moment(last(this.dateRange));
    return l.diff(h, "days") + 1;
  }

  get isInternational() {
    return this.app.app.international;
  }

  private tabChange$ = this.calendarSrv.sidebarTabRaw$.pipe(
    distinctUntilChanged(),
    tap((value) => this.updateTabIndex(value.type)),
  );

  private dateChange$ = this.calendarSrv.selectRaw$.pipe(
    distinctUntilChanged(),
    tap((value) => {
      this.updateDateRange(value);
    }),
    share(),
  );

  private destroy$ = new Subject();

  constructor(
    private calendarSrv: MultiHouseCalendarService,
    private deprecatedSrv: HttpCalendarService,
    private statusSrv: HouseStatusService,
    private store: StoreService,
    private app: AppService,
  ) {}

  ngOnInit() {
    this.tabChange$.pipe(takeUntil(this.destroy$)).subscribe();
    this.dateChange$.pipe(takeUntil(this.destroy$)).subscribe();

    const data = this.selectedData ? this.selectedData : this.calendarSrv.selectedData;
    console.log("selected data: ", this.calendarSrv.selectedData);
    this.updateDateRange(data);
    this.updateEntityData(data);

    if (this.entityType === "house") {
      this.fetchForbiddenDateList();
      this.fetchHouseAndTypeInfo();
    }
  }

  private updateEntityData(data: SelectedData<entityType, "status">) {
    this.entity = data.data.payload;
    this.entityType = data.data.type;
    console.log("entity: ", this.entity, "entityType: ", this.entityType);
  }

  private updateDateRange(value: SelectedData<entityType, "status">) {
    const dates = value.dates;
    const head: Moment = get(dates, "head") || moment();
    const last: Moment = get(dates, "last") || moment();

    this.dateRange = [head.toDate(), last.toDate()];
  }

  /**
   * 房态页获取房间 id 关联的房间/房型，并进一步获取关联房间/ 房型的第三方房源的价格列表
   */
  private fetchHouseAndTypeInfo() {
    const house_id = (this.entity as Child).c_id;
    this.deprecatedSrv.getHouseAndTypeInfo({ house_id }).subscribe((res) => {
      const { house, house_type } = res?.data || {};

      const result = [
        ...(house ? [{ type: "house", id: house.id, title: house.house_title }] : []),
        ...(house_type ? [{ type: "type", ...house_type }] : []),
      ];

      this.listOfHostexPriceHouses = result;
      this.listOfRulesRelevantUnits = result;

      console.log("list of hostex price houses: ", this.listOfHostexPriceHouses);
      this.fetchPriceList(
        result.filter((item) => item.type === "house").map((item) => item.id),
        result.filter((item) => item.type === "type").map((item) => item.id),
      );
    });
  }

  /**
   *  房态改价获取价格接口
   *  房态改价页需要 listOfHostexPricePrices
   *  获取该数据之后，过滤出符合条件的数据，就是 listOfRulesSettingHouse，房态预定规则需要
   */
  fetchPriceList(house_ids = [], house_type_ids = []) {
    if (isEmpty(house_ids) && isEmpty(house_type_ids)) {
      this.listOfHostexPricePrices = [];
      this.updateTabIndex();
      return;
    }

    const [begin, end] = [
      moment(head(this.dateRange)).format("YYYY-MM-DD"),
      moment(last(this.dateRange)).format("YYYY-MM-DD"),
    ];

    const params = { begin, end, type: "house_status", version: "4.0.2" };

    if (!isEmpty(house_ids)) {
      Object.assign(params, { house_ids });
    }

    if (!isEmpty(house_type_ids)) {
      Object.assign(params, { house_type_ids });
    }
    this.deprecatedSrv.getPriceList(params).subscribe((res) => {
      this.listOfHostexPricePrices = res?.data?.list || [];

      console.log("list of hostex price: ", this.listOfHostexPricePrices);
      // this.listOfRulesSettingHouse = this.listOfHostexPricePrices?.filter((item) =>
      //   [PlatformType.Booking, PlatformType.Agoda, PlatformType.Airbnb].includes(item.thirdparty_type)
      // );

      // console.log('list of rules setting house: ', this.listOfRulesSettingHouse);
      this.updateTabIndex();
    });
  }

  private fetchForbiddenDateList() {
    const house_id = (this.entity as Child).c_id;
    this.statusSrv.getForbiddenDateList({ house_id }).subscribe((res) => {
      this.listOfForbiddenDates = res.data;
      console.log("[operate sidebar forbidden dates]: ", this.listOfForbiddenDates);
    });
  }

  updateTabIndex(name?: TabName) {
    console.log("updateTabIndex: ", name);

    const map: Partial<Record<TabName, number>> = {
      "manual-order": 0,
      "hostex-status": 1,
      "hostex-price": 2,
      "rules-setting": 3,
    };

    const key = "operate-sidebar-tab-name";
    const value = this.store.get(key, 2) || 0;
    console.log("updateTabIndex get key from store: ", value);
    if (name !== undefined) {
      this.store.set(key, name, 2);
      this.index = map[name];
    } else {
      this.index = map[value];
    }
  }

  onClickCancel() {
    this.calendarSrv.cancelSelect();
  }
}
